import { inject } from '@angular/core';
import { IsActiveMatchOptions, Navigation, Router } from '@angular/router';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { Logger } from '@frontend/vanilla/core';

import {
    EXCLUDE_BETSLIP_CLASS,
    EXCLUDE_BOTTOM_NAV_CLASS,
    EXCLUDE_HEADER_CLASS,
    EXCLUDE_NONE_CLASS,
    EXCLUDE_QUICK_BET_CLASS,
    VIEW_TRANSITION_LOG_TAG as LOG_TAG,
    TRANSITION_PATHS,
} from './view-transition.constants';

type NavigationInfo =
    | {
          [k: string]: any;
          isBackNavigation: boolean;
      }
    | undefined;

/** Indicates whether the current navigation is to the active page */
export const isNavigationToCurrentPage = (): boolean => {
    const logger = inject(Logger);
    const router = inject(Router);
    const incomingUrl = router.getCurrentNavigation()?.finalUrl;

    if (!incomingUrl) {
        logger.error(`${LOG_TAG} isNavigationToCurrentPage called outside the context of an active navigation`);

        return false;
    }

    const urlMatchingOptions: IsActiveMatchOptions = {
        paths: 'exact',
        matrixParams: 'exact',
        fragment: 'ignored',
        queryParams: 'ignored',
    };

    const _isNavigationToCurrentPage = router.isActive(incomingUrl, urlMatchingOptions);

    if (_isNavigationToCurrentPage) {
        logger.debug(`${LOG_TAG} Detected navigation to the current page`);
    }

    return _isNavigationToCurrentPage;
};

/** Indicates whether the current navigation is to a page for which a full-slide transition should be played */
export const isNavigationToOrFromFullSlidePage = (): boolean => {
    if (!isEpcotEnabled()) {
        return isPathInNavigation(TRANSITION_PATHS.AC_MENU_PATH) || isPathInNavigation(TRANSITION_PATHS.AC_MENU_OVERLAY_PATH);
    }

    if (isPathInNavigation(TRANSITION_PATHS.EVENTS_PATH, { both: true })) {
        return false; // never full-slide from one event to another
    }

    return (
        isPathInNavigation(TRANSITION_PATHS.AC_MENU_PATH) ||
        isPathInNavigation(TRANSITION_PATHS.AC_MENU_OVERLAY_PATH) ||
        isPathInNavigation(TRANSITION_PATHS.EVENTS_PATH)
    );
};

/**
 * Modifies which elements are included in the full-slide transition based on label and route
 */
export const getFullSlideModifier = (): string[] => {
    if (isEpcotEnabled()) {
        if (isPathInNavigation(TRANSITION_PATHS.AC_MENU_PATH)) {
            return [EXCLUDE_BOTTOM_NAV_CLASS];
        }

        if (isPathInNavigation(TRANSITION_PATHS.EVENTS_PATH)) {
            return [EXCLUDE_BOTTOM_NAV_CLASS, EXCLUDE_BETSLIP_CLASS, EXCLUDE_QUICK_BET_CLASS];
        }
    } else {
        if (isPathInNavigation(TRANSITION_PATHS.AC_MENU_PATH) || isPathInNavigation(TRANSITION_PATHS.AC_MENU_OVERLAY_PATH)) {
            return [EXCLUDE_NONE_CLASS];
        }

        if (isPathInNavigation(TRANSITION_PATHS.EVENTS_PATH)) {
            return [EXCLUDE_BOTTOM_NAV_CLASS, EXCLUDE_BETSLIP_CLASS, EXCLUDE_QUICK_BET_CLASS];
        }
    }

    return [EXCLUDE_NONE_CLASS];
};

/**
 * Modifies which elements are included in the mini-slide transition based on label and route
 */
export const getMiniSlideModifier = (): string | null => {
    if (!isEpcotEnabled() && isPathInNavigation(TRANSITION_PATHS.AZ_MENU_PATH)) {
        return EXCLUDE_HEADER_CLASS;
    }

    return null; // no modification to the transitioning elements
};

/** Indicates whether the current navigation is an in-app 'back' navigation (e.g. in-app back or breadcrumbs button click) */
export const isNavigationApplicationBack = (): boolean => {
    if (isNavigationTraversal()) {
        return false;
    }

    const logger = inject(Logger);
    const router = inject(Router);
    const currentNavigation = router.getCurrentNavigation();

    if (!currentNavigation) {
        logger.error(`${LOG_TAG} isNavigationApplicationBack called outside the context of an active navigation`);

        return false;
    }

    return isNavigationFlaggedBack(currentNavigation) || isNavigationToAncestorRoute(currentNavigation);
};

const isEpcotEnabled = (): boolean => {
    const epcotConfig = inject(EpcotConfig);

    return epcotConfig.isEnabled;
};

const isPathInNavigation = (path: string, options = { both: false }): boolean => {
    const logger = inject(Logger);
    const router = inject(Router);
    const currentNavigation = router.getCurrentNavigation();

    if (!currentNavigation) {
        logger.error(`${LOG_TAG} isPathInNavigation called outside the context of an active navigation`);

        return false;
    }

    const currentUrl = currentNavigation.finalUrl?.toString();
    const previousUrl = currentNavigation.previousNavigation?.finalUrl?.toString();

    if (options.both) {
        return Boolean(currentUrl?.includes(path) && previousUrl?.includes(path));
    }

    return Boolean(currentUrl?.includes(path) || previousUrl?.includes(path));
};

/** Indicates whether the current navigation is a traversal through existing history (i.e. browser back or forward navigations) */
const isNavigationTraversal = (): boolean => {
    const isNavigationTraversal = inject(Router).getCurrentNavigation()?.trigger === 'popstate';

    return isNavigationTraversal;
};

const isNavigationFlaggedBack = (currentNavigation: Navigation): boolean => {
    const logger = inject(Logger);

    const isNavigationFlaggedBack = Boolean((currentNavigation?.extras?.info as NavigationInfo)?.isBackNavigation);

    if (isNavigationFlaggedBack) {
        logger.debug(`${LOG_TAG} The current navigation is flagged as an in-app back navigation`);
    }

    return isNavigationFlaggedBack;
};

const isNavigationToAncestorRoute = (currentNavigation: Navigation): boolean => {
    const logger = inject(Logger);

    const currentUrl = currentNavigation.finalUrl?.toString();
    const previousUrl = currentNavigation.previousNavigation?.finalUrl?.toString();
    const isAncestorRoute = currentUrl !== undefined && previousUrl?.includes(currentUrl);

    if (isAncestorRoute) {
        logger.debug(`${LOG_TAG} The current navigation is to an ancestor of the previous URL`);
    }

    return Boolean(isAncestorRoute);
};
